/**
 * # Referee ledger — the read side of the dark dual-run, and the loud breaker
 *
 * The default binder computes TWO structure programs and TWO cell verdicts every
 * sampled full-rebuild bind and referees them (`refereeCommit` / `refereeCells`).
 * Before this module the answer went into `LiveSceneRunner`'s pull-based tallies
 * and a `Tracing` breadcrumb with no reader, and a `loud` verdict had no
 * consequence: the flag alone picked the bundle, so a divergent scene program
 * would have shipped the wrong DOM and been recorded as a counter. The live-flip
 * gate wants "loud stays 0" as EVIDENCE, which needs a durable readable home and
 * a loud verdict that costs something.
 *
 * This module is both:
 *
 *   1. **Counts** per binder instance, in the referees' OWN verdict vocabularies
 *      — structure `exact` / `slide-classified` / `loud` (`RefereeVerdict`), cell
 *      `agree` / `loud` (`CellRefereeVerdict`) — plus the dark-pipeline sampling
 *      outcomes (`sampledIn` / `sampledOut` / `stickyForced`) and the `bootstrap`
 *      count that explains a thin cell-referee corpus.
 *   2. **The circuit breaker**: the first `loud` structure verdict latches this
 *      instance onto the positional bundle (`selectStructureSource`) until an
 *      explicit `resetBreaker()`. The dark pipeline keeps running while latched,
 *      so evidence keeps accruing — only the SELECTION changes. Recording that
 *      verdict IS the latch (`recordCommit`), so the promise holds for every
 *      caller rather than only for the binder that happens to call
 *      `tripBreaker()` next; `tripBreaker()` stays as the explicit, idempotent
 *      latch and hands out the single announcement per latch.
 *   3. **The sampling admission** (`sampleAdmits`), whose rate is an
 *      {@link AuditRate} — a branded `[0, 1]` validated by its only constructor
 *      — so a misconfigured rate throws where it used to silently disable
 *      (`NaN`) or saturate (`> 1`) the whole evidence corpus. `sampleAdmits`
 *      accepts the BRAND and nothing else; each caller parses once at the edge
 *      the plain number crosses.
 *
 * Every instance rolls up into a module-level aggregate readable through the
 * static `RefereeLedger.snapshot()` — the accessor dev tooling reads (the
 * `LiveSceneRunner` tallies stay exactly as they are; this is the layer above).
 *
 * Hot path is allocation-light: recording a verdict is integer increments only.
 * `snapshot()` is the sole allocation and is called from already-gated log sites
 * and at breaker-trip time, never once per bind.
 *
 * Pure + DOM-free (types only, from `commit-referee`), so it loads under Deno
 * alongside the referees it aggregates.
 */

import type { Option } from "@tento-lona/sheets";
import type {
  CellRefereeDivergence,
  CellRefereeResult,
  RefereeDivergence,
  RefereeResult,
} from "./commit-referee.ts";

/** Which compiled structure program a bind's `applyStructure` consumes. */
export type StructureSource = "scene" | "positional";

/**
 * Bundle selection for one bind — the breaker's only behavioral surface.
 *
 * `session: "diagnostics"` (the sheet's `registries.render.diagnostics()`) is
 * the dev / measurement surface and must keep showing the REAL scene path,
 * divergences and all, so the breaker deliberately does not govern it. The
 * breaker governs the PRODUCTION flag only: with `flag: "dormant"` (today) it is
 * dormant, and it becomes load-bearing the day the SDK 2.0 switch goes live.
 */
export function selectStructureSource(state: StructureSelection): StructureSource {
  if (state.session === "diagnostics") return "scene";
  return state.flag === "live" && state.breaker === "closed" ? "scene" : "positional";
}

/** What one bind's `selectStructureSource` decides on, each fact a named state. */
export type StructureSelection = {
  /** The SDK 2.0 structure flag (`registries.render.live()`). */
  readonly flag: "live" | "dormant";
  /** A measurement session (`registries.render.diagnostics()`) or production. */
  readonly session: "diagnostics" | "production";
  /** The referee breaker: `"tripped"` once a loud verdict pinned positional. */
  readonly breaker: "tripped" | "closed";
};

/**
 * A validated audit sampling rate in `[0, 1]`: `0` means never, `1` means
 * always, and everything between is the fraction of builds admitted by a coin.
 *
 * It is BRANDED because the precondition used to live in a doc comment while
 * the slot was a bare `number`, and every way of violating it failed silently
 * (sdk-0's finding H3): `NaN >= 1` is false and `roll() < NaN` is false, so a
 * `NaN` rate mapped to always-out and disabled evidence collection entirely,
 * while `1.5` mapped to always-in and made every bind pay for the dark
 * pipeline. Neither logged anything. A misconfigured rate is now a throw at the
 * boundary the number crosses, which is the only place it can still be a plain
 * `number`.
 */
export type AuditRate = number & { readonly __brand: "AuditRate0To1" };

/** Values that go with `AuditRate`: `NEVER`, `ALWAYS`, `parse`. */
export namespace AuditRate {
  /** Sample nothing. */
  export const NEVER = 0 as AuditRate;
  /** Sample everything — the live / debug-override configuration. */
  export const ALWAYS = 1 as AuditRate;

  /**
   * The ONLY constructor. Accepts a finite number in `[0, 1]`; rejects `NaN`,
   * `Infinity`, negatives and anything above `1` with a `RangeError` naming the
   * offending value. Idempotent on an already-branded rate, so a value may be
   * re-validated at an inner boundary for free.
   */
  export function parse(rate: number): AuditRate {
    if (!Number.isFinite(rate)) {
      throw new RangeError(
        `AuditRate.parse: rate must be a finite number in [0, 1], got ${rate}`,
      );
    }
    if (rate < 0 || rate > 1) {
      throw new RangeError(
        `AuditRate.parse: rate must be in [0, 1], got ${rate}`,
      );
    }
    return rate as AuditRate;
  }
}

/**
 * Whether the dark scene pipeline runs for this build.
 *
 * `forced` is the STICKY paired-sampling mark (see `LiveSceneRunner.build`): the
 * successor of a build that bootstrapped runs regardless of the coin, because a
 * bootstrap produces no diff and therefore no cell-referee corpus. A forced build
 * is emphatically NOT a sampled-out build — it keeps the retained prior scene,
 * which is the entire point of forcing it.
 *
 * `roll` is a THUNK, mirroring the `rate` thunk, so the `forced` and full-rate
 * short circuits never draw from the PRNG — the live / debug-override path keeps
 * its exact pre-sampling behavior.
 *
 * `rate` is an {@link AuditRate} and nothing else. It used to be declared
 * `AuditRate | number` and validated here, which made the brand ceremonial: a
 * type whose sole consumer also accepts the unvalidated form is a doc comment
 * with a `__brand` field. Every caller now parses ONCE, at the edge the plain
 * number crosses — for the production path that is `LiveSceneRunner`, which
 * resolves `darkSceneSampleRate()`'s `() => number` thunk fresh per build and
 * parses the result before calling in.
 *
 * That also turns a hand-placed statement order into a property of the
 * signature. The old body validated BEFORE the `forced` short circuit, on the
 * argument that a misconfigured rate is a misconfiguration on every build and a
 * signal that fires only on unforced builds is one nobody can act on; now a bad
 * rate cannot be spelled at the call site at all, forced or not, and no PRNG is
 * drawn on the way to finding out.
 */
export function sampleAdmits(
  rate: AuditRate,
  forced: boolean,
  roll: () => number,
): boolean {
  if (forced) return true;
  if (rate >= AuditRate.ALWAYS) return true;
  if (rate <= AuditRate.NEVER) return false;
  return roll() < rate;
}

/**
 * The read side of the binder's dark dual run: tallies the referee's verdicts and trips a breaker
 * on a divergence, for inspection tools.
 */
export class RefereeLedger {
  /**
   * Process-wide roll-up across every binder instance, read through the static
   * `RefereeLedger.snapshot()`. Its own `rollUp` is null, which terminates the
   * forwarding chain. It holds no breaker (policy is per instance), so its
   * `breakerTripped` is always false; `breakerTrips` counts how many instances
   * have latched.
   */
  static readonly #aggregate = new RefereeLedger(null);

  // ── structure referee (`RefereeVerdict`) ──────────────────────────
  #exact = 0;
  #slideClassified = 0;
  #loud = 0;
  /** Scene compiles with no trustworthy prior (pool-vs-target membership). */
  #bootstrap = 0;

  // ── cell referee (`CellRefereeVerdict`) ───────────────────────────
  #cellAgree = 0;
  #cellLoud = 0;

  // ── dark-pipeline sampling ────────────────────────────────────────
  #sampledIn = 0;
  #sampledOut = 0;
  #stickyForced = 0;

  // ── breaker ───────────────────────────────────────────────────────
  #tripped = false;
  #trips = 0;
  /**
   * The once-per-latch announcement token. Latching and ANNOUNCING are two
   * facts, and only the latch is automatic: `recordCommit` latches the instant
   * a loud verdict is recorded, while the caller that wants to raise the alarm
   * claims this token through `tripBreaker()`. Splitting them is what lets the
   * latch be atomic without silently swallowing the binder's one `Tracing.err`.
   */
  #announcement = false;

  // ── bounded first-loud evidence (kept, never overwritten) ─────────
  #firstLoudCommit: Option<RefereeDivergence> = null;
  #firstLoudCell: Option<CellRefereeDivergence> = null;

  readonly #rollUp: Option<RefereeLedger>;
  private constructor(rollUp: Option<RefereeLedger>) {
    this.#rollUp = rollUp;
  }

  /** A fresh per-binder-instance ledger that also feeds the module aggregate. */
  static forBinder(): RefereeLedger {
    return new RefereeLedger(RefereeLedger.#aggregate);
  }

  /** The process-wide aggregate across every binder instance (dev tooling). */
  static snapshot(): RefereeLedger.Counters {
    return RefereeLedger.#aggregate.snapshot();
  }

  /**
   * Record one structure-referee verdict AND, when it is `loud`, latch this
   * instance's breaker — one operation, not two. MIRRORS (does not replace)
   * `LiveSceneRunner.recordReferee`; this is the durable side the breaker and
   * the dev accessor read.
   *
   * The module header promises "the first `loud` structure verdict latches this
   * instance". Until this became atomic that promise was kept by the BINDER,
   * which calls `tripBreaker()` on the next line — true for that one caller and
   * for nobody else, so any second caller would have recorded loud verdicts
   * against a breaker that never latched (sdk-0's finding C3). Recording is the
   * only way a loud verdict enters the ledger, so it is the right place for the
   * latch.
   *
   * The ANNOUNCEMENT is deliberately not folded in: `tripBreaker()` still
   * returns `true` exactly once per latch, so the binder's `Tracing.err` fires
   * on the same bind, once, exactly as before.
   */
  recordCommit(result: RefereeResult, bootstrapped: boolean): void {
    this.#tallyCommit(result, bootstrapped);
    if (result.verdict === "loud") this.#latch();
  }

  /**
   * The counting half of {@link recordCommit}, forwarded up the roll-up chain.
   * The aggregate is counted THROUGH this and never through `recordCommit`, so
   * a per-instance latch is never mistaken for a process-wide one — the
   * aggregate holds no policy and its `breakerTripped` stays false.
   */
  #tallyCommit(result: RefereeResult, bootstrapped: boolean): void {
    switch (result.verdict) {
      case "exact":
        this.#exact += 1;
        break;
      case "slide-classified":
        this.#slideClassified += 1;
        break;
      case "loud":
        this.#loud += 1;
        if (this.#firstLoudCommit == null) {
          this.#firstLoudCommit = result.firstDivergence;
        }
        break;
    }
    if (bootstrapped) this.#bootstrap += 1;
    const rollUp = this.#rollUp;
    if (rollUp != null) rollUp.#tallyCommit(result, bootstrapped);
  }

  /** Tally one cell-referee verdict (mirrors `LiveSceneRunner.recordCellReferee`). */
  recordCell(result: CellRefereeResult): void {
    switch (result.verdict) {
      case "agree":
        this.#cellAgree += 1;
        break;
      case "loud":
        this.#cellLoud += 1;
        if (this.#firstLoudCell == null) {
          this.#firstLoudCell = result.firstDivergence;
        }
        break;
    }
    const rollUp = this.#rollUp;
    if (rollUp != null) rollUp.recordCell(result);
  }

  /** One dark build ran. `forced` ⇒ it ran on the sticky mark, not on the coin. */
  recordSampledIn(forced: boolean): void {
    this.#sampledIn += 1;
    if (forced) this.#stickyForced += 1;
    const rollUp = this.#rollUp;
    if (rollUp != null) rollUp.recordSampledIn(forced);
  }

  /** One dark build the coin dropped (its retained prior scene is dropped too). */
  recordSampledOut(): void {
    this.#sampledOut += 1;
    const rollUp = this.#rollUp;
    if (rollUp != null) rollUp.recordSampledOut();
  }

  /** Whether this instance's breaker is latched (⇒ the flag path pins positional). */
  breakerTripped(): boolean {
    return this.#tripped;
  }

  /**
   * Latch the breaker explicitly, and claim the announcement. Returns `true`
   * EXACTLY ONCE per latch, so the caller's alarm fires at trip time rather
   * than once per bind — whether the latch happened here or a moment earlier
   * inside {@link recordCommit}. Idempotent: further calls return `false` until
   * a `resetBreaker()` re-arms it. Further loud verdicts keep accruing into the
   * counts; only the announcement is deduplicated.
   */
  tripBreaker(): boolean {
    this.#latch();
    if (!this.#announcement) return false;
    this.#announcement = false;
    return true;
  }

  /**
   * Re-arm: selection returns to the flag and the next loud verdict announces
   * again. Deliberately EXPLICIT and session-scoped — a self-healing breaker
   * would let a divergent scene program ship between trips. Counts and the
   * first-divergence evidence survive the reset; an unclaimed announcement does
   * not (it belongs to the latch being cleared).
   */
  resetBreaker(): void {
    this.#tripped = false;
    this.#announcement = false;
  }

  /** Pull-based snapshot for the alarm payload, gated breadcrumbs, and tests. */
  snapshot(): RefereeLedger.Counters {
    return {
      exact: this.#exact,
      slideClassified: this.#slideClassified,
      loud: this.#loud,
      bootstrap: this.#bootstrap,
      cellAgree: this.#cellAgree,
      cellLoud: this.#cellLoud,
      sampledIn: this.#sampledIn,
      sampledOut: this.#sampledOut,
      stickyForced: this.#stickyForced,
      breakerTripped: this.#tripped,
      breakerTrips: this.#trips,
      firstLoudCommit: this.#firstLoudCommit,
      firstLoudCell: this.#firstLoudCell,
    };
  }

  /**
   * Latch on the false→true edge only: pin the selection, count the trip, and
   * arm the one announcement `tripBreaker()` hands out. A no-op once latched.
   */
  #latch(): void {
    if (this.#tripped) return;
    this.#tripped = true;
    this.#announcement = true;
    this.#noteTrip();
  }

  /** Count a latch here and in the aggregate (which holds no breaker of its own). */
  #noteTrip(): void {
    this.#trips += 1;
    const rollUp = this.#rollUp;
    if (rollUp != null) rollUp.#noteTrip();
  }
}

/** Types that go with `RefereeLedger`: `Counters`. */
export namespace RefereeLedger {
  /** The pull aggregate the alarm payload, dev tooling, and tests read. */
  export interface Counters {
    /** Structure referee: byte-outcome-equal bundles. */
    readonly exact: number;
    /** Structure referee: the one benign same-final divergence (survivor slide). */
    readonly slideClassified: number;
    /** Structure referee: a real divergence — the breaker's trip condition. */
    readonly loud: number;
    /** Scene compiles with no trustworthy prior (⇒ all-rebind, no cell corpus). */
    readonly bootstrap: number;
    /** Cell referee: every sampled claim held. */
    readonly cellAgree: number;
    /** Cell referee: a skip / resize / span claim the ground truth rejected. */
    readonly cellLoud: number;
    /** Dark builds that ran (coin-admitted plus sticky-forced). */
    readonly sampledIn: number;
    /** Dark builds the coin dropped. */
    readonly sampledOut: number;
    /** Of `sampledIn`, the ones the sticky paired-sampling mark forced. */
    readonly stickyForced: number;
    /** Whether the breaker is latched right now (always false on the aggregate). */
    readonly breakerTripped: boolean;
    /** How many times a breaker latched (aggregate: across all instances). */
    readonly breakerTrips: number;
    readonly firstLoudCommit: Option<RefereeDivergence>;
    readonly firstLoudCell: Option<CellRefereeDivergence>;
  }
}
